import React, { Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Container } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { polyfillCSS } from "../../../utils/css";
import clsx from "clsx";
import { PageSkeleton } from "../../../components/loading/PageSkeleton";
import { CoachProgramLibraryHeaderFilters } from "../../../components/program-library/CoachProgramLibraryHeaderFilters";
import {
  AllOption,
  AllOptionType,
  ProgramListFilterContext,
  ProgramTemplateFilterType,
} from "../../../contexts/ProgramLibraryFilterContext";
import { ProgramTemplateCategory } from "@growth-machine-llc/stridist-api-client";
import { ProgramTemplateSort } from "../../../components/program-library/filters/ProgramNameSortButton";
import { JourneyAnchors } from "../../../constants";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(11),
    paddingLeft: polyfillCSS(
      `calc(${theme.spacing(4)} + var(--safe-area-inset-left)) !important`,
    ),
    [theme.breakpoints.down("lg")]: {
      paddingLeft: "20px !important",
      paddingRight: "20px !important",
    },
  },
  filters: {
    marginBottom: theme.spacing(2),
  },
}));

export interface ProgramLibraryFilters {
  category: ProgramTemplateCategory[] | AllOptionType;
  templateType: ProgramTemplateFilterType;
}

export const PROGRAM_LIBRARY_FILTERS_KEY = "programLibraryFilters";

const CoachProgramLibraryWrapper = () => {
  const location = useLocation();
  const s = useStyles();

  const defaultFilters = {
    query: "",
    sortKey: ProgramTemplateSort.NAME_ASC,
  };

  const defaultTableFilters: ProgramLibraryFilters = {
    category: AllOption,
    templateType: AllOption,
  };

  const [query, setQuery] = React.useState(defaultFilters.query);
  const [sortKey, setSortKey] = React.useState<ProgramTemplateSort>(
    defaultFilters.sortKey,
  );

  const [filters, setFilters] =
    React.useState<ProgramLibraryFilters>(defaultTableFilters);

  const { category, templateType } = filters;

  const updateFilters = (newFilters: Partial<ProgramLibraryFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const handleQueryChange = React.useCallback(
    (query: string) => {
      setQuery(query);
    },
    [setQuery],
  );

  const handleSortKeyChange = React.useCallback(
    (sortKey: ProgramTemplateSort) => {
      setSortKey(sortKey);
    },
    [setSortKey],
  );

  const handleCategoryChange = React.useCallback(
    (category: ProgramTemplateCategory[] | AllOptionType) => {
      // If all categories are selected, set to ALL_OPTIONS
      const allCategories = Object.values(ProgramTemplateCategory);
      const isAllSelected =
        Array.isArray(category) &&
        category.length === allCategories.length &&
        allCategories.every((c) => category.includes(c));

      updateFilters({
        category: isAllSelected ? AllOption : category,
      });
    },
    [updateFilters],
  );

  const handleTemplateTypeChange = React.useCallback(
    (type: ProgramTemplateFilterType) => {
      updateFilters({ templateType: type });
    },
    [updateFilters],
  );

  React.useEffect(() => {
    setQuery(defaultFilters.query);
    setSortKey(defaultFilters.sortKey);
  }, [location.pathname]);

  const filtersToReset =
    category !== AllOption ||
    templateType !== defaultTableFilters.templateType ||
    query !== defaultFilters.query ||
    sortKey !== defaultFilters.sortKey;

  const handleResetFilters = React.useCallback(() => {
    updateFilters({
      category: AllOption,
      templateType: AllOption,
    });
    setQuery(defaultFilters.query);
    setSortKey(defaultFilters.sortKey);
  }, [updateFilters]);

  const filterContextValue = {
    query,
    sortKey,
    category,
    templateType,
    handleQueryChange,
    handleSortKeyChange,
    handleCategoryChange,
    handleTemplateTypeChange,
  };

  return (
    <ProgramListFilterContext.Provider value={filterContextValue}>
      <Container
        className={clsx(s.root, JourneyAnchors.PROGRAM_LIBRARY_TEMPLATES_LIST)}
        maxWidth="xl"
      >
        <CoachProgramLibraryHeaderFilters
          className={s.filters}
          query={query}
          sortKey={sortKey}
          onChangeQuery={handleQueryChange}
          onChangeSortKey={handleSortKeyChange}
          templateType={templateType}
          onChangeTemplateType={handleTemplateTypeChange}
          category={category}
          onChangeCategory={handleCategoryChange}
          disabledResetButton={!filtersToReset}
          showResetButton={true}
          onResetFilters={handleResetFilters}
          defaultValues={{
            ...defaultFilters,
            category: defaultTableFilters.category,
            templateType: defaultTableFilters.templateType,
          }}
        />
        <Suspense fallback={<PageSkeleton fullWidth />}>
          <Outlet />
        </Suspense>
      </Container>
    </ProgramListFilterContext.Provider>
  );
};

export default CoachProgramLibraryWrapper;
